import React, { useState, useEffect, useContext, useCallback } from "react";
import { Grid, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import TextField from "@jll/textfield";
import MButton from "@material-ui/core/Button";
import { recordTypes } from "../../../../API/config";
import { getCountries, getGeographies } from "../../../../API/Country";
import {
  getRecordAccessRules,
  getAllUserGroups,
  removeRecordAccessRule,
} from "../../../../API/AccessControl";
import { getAllSequences } from "../../../../API/Process";
import { getListValues } from "../../../../API/List";
import { NoAccessScreen, LoadingIndicator } from "../../../../Component";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import { validateJllEmail } from "../../../../Utilities/Functions";
import ViewRule from "./Components/ViewRule";
import ViewUserAccess from "./Components/ViewUserAccess";
import AddRule from "./Components/AddRule";
import RulesTable from "./Components/RulesTable";
import styles from "./styles";

const useStyles = makeStyles(styles);

const AccessControlRules = (props) => {
  const { hasPermission } = props;

  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [currentRule, setCurrentRule] = useState();
  const [isNewRule, setIsNewRule] = useState(false);
  const [isViewRule, setIsViewRule] = useState(false);
  const [isViewUserAccess, setIsViewUserAccess] = useState(false);

  const [rules, setRules] = useState([]);
  const [loadRules, setLoadRules] = useState(false);
  const [userEmail, setUserEmail] = useState();
  const [userEmailError, setUserEmailError] = useState();

  const [canViewRules, setCanViewRules] = useState();
  const [canAddRule, setCanAddRule] = useState();
  const [canUpdateRule, setCanUpdateRule] = useState();
  const [canRemoveRule, setCanRemoveRule] = useState();

  const [geographiesData, setGeographiesData] = useState([]);
  const [entityCategories, setEntityCategories] = useState([]);
  const [entitySubCategories, setEntitySubCategories] = useState([]);
  const [countriesDataForAccessRole, setCountriesDataForAccessRole] = useState(
    [],
  );
  const [processSequencesForAccessRole, setProcessSequencesForAccessRole] =
    useState([]);
  const [allUserGroups, setAllUserGroups] = useState([]);

  const { showErrorMessage, showConfirmation, showMessage } =
    useContext(ModalDialogContext);

  const { t } = useTranslation();
  const handleClose = useCallback(
    (status) => {
      setIsViewRule(false);
      setIsNewRule(false);
      setIsViewUserAccess(false);
    },
    [setIsViewRule, setIsNewRule, setIsViewUserAccess],
  );

  const viewRule = useCallback(
    async (rule) => {
      setIsViewUserAccess(false);
      setCurrentRule(rule);
      setIsViewRule(true);
    },
    [setIsViewUserAccess, setCurrentRule, setIsViewRule],
  );

  const addRule = useCallback(async () => {
    setIsNewRule(true);
  }, [setIsNewRule]);

  const onDelete = useCallback(
    async (ruleToDelete) => {
      showConfirmation({
        message: t("messages.accessControl.deleteRule"),
        onPressYes: async (event) => {
          setLoading(true);
          try {
            if (ruleToDelete.id === "2") {
              throw new Error(
                `${t(`messages.common.errors.cannotDeleteAdminRule`)}`,
              );
            }
            await removeRecordAccessRule({ id: ruleToDelete.id });
            showMessage({
              message: t("messages.accessControl.ruleDeleted"),
            });

            setLoadRules(true);
          } catch (error) {
            showErrorMessage({ error });
            setLoading(false);
          }
        },
      });
      setLoading(false);
    },
    [showConfirmation, showErrorMessage],
  );

  useEffect(() => {
    const setPermissions = async () => {
      const canViewRules = await hasPermission(
        recordTypes.accessRule.name,
        recordTypes.accessRule.actions.view.name,
      );
      setCanViewRules(canViewRules);
      const canAddRule = await hasPermission(
        recordTypes.accessRule.name,
        recordTypes.accessRule.actions.create.name,
      );
      setCanAddRule(canAddRule);
      const canUpdateRule = await hasPermission(
        recordTypes.accessRule.name,
        recordTypes.accessRule.actions.update.name,
      );
      setCanUpdateRule(canUpdateRule);
      const canRemoveRule = await hasPermission(
        recordTypes.accessRule.name,
        recordTypes.accessRule.actions.remove.name,
      );
      setCanRemoveRule(canRemoveRule);
    };

    setPermissions();

    const loadGeographies = async () => {
      try {
        const result = await getGeographies();
        const { data = [] } = result;
        const geographies = data.map((val) => ({
          isTicked: false,
          value: val.id,
          label: val.name,
        }));
        setGeographiesData(geographies);
      } catch (error) {
        console.log(error);
      }
    };
    loadGeographies();

    const loadEntityCategories = async (listId, stateToPutData) => {
      try {
        const listResult = await getListValues({ listId });
        const { data = [] } = listResult;
        const entities = data.map((val) => ({
          label: val.code,
          value: val.value,
          isTicked: false,
          id: val.id,
          listId: val.list_id,
        }));
        stateToPutData(entities);
      } catch (error) {
        console.log(error);
      }
    };
    loadEntityCategories("1", setEntityCategories);
    loadEntityCategories("21", setEntitySubCategories);

    const loadCountries = async () => {
      try {
        const result = await getCountries();
        const { data = [] } = result;
        setCountriesDataForAccessRole(
          data.map((val) => ({
            value: val.id,
            label: val.name,
            isTicked: false,
          })),
        );
      } catch (error) {
        console.log(error);
      }
    };
    loadCountries();

    const loadAllProcessSequences = async () => {
      try {
        const result = await getAllSequences();
        const { data = [] } = result;
        setProcessSequencesForAccessRole(
          data.map((val) => ({
            value: val.name,
            label: val.label,
            isTicked: false,
          })),
        );
      } catch (error) {
        console.log(error);
      }
    };
    loadAllProcessSequences();
    //API call for all roles
    const getAllSecuGroups = async () => {
      try {
        const result = await getAllUserGroups();
        const { data = [] } = result;

        setAllUserGroups(
          data.map((val) => ({
            label: val.name,
            value: val.name,
            roleid: val.id,
            roletype: val.roleType,
          })),
        );
      } catch (error) {
        console.log(error);
      }
    };
    getAllSecuGroups();
    setLoadRules(true);
  }, []);

  useEffect(() => {
    const loadRules = async () => {
      try {
        setLoading(true);
        const result = await getRecordAccessRules();
        const { data = [] } = result;
        setRules(data);
        setLoading(false);
        setLoadRules(false);
      } catch (error) {
        showErrorMessage({ error });
        setLoading(false);
        setLoadRules(false);
      }
    };
    if (loadRules) {
      loadRules();
    }
  }, [loadRules]);

  if (!loading && !canViewRules) {
    return <NoAccessScreen />;
  }

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const searchUser = async () => {
    if (!userEmail) {
      setUserEmailError(t("messages.accessControl.emailError"));
      return;
    }
    if (!validateJllEmail(userEmail)) {
      setUserEmailError(t("messages.accessControl.emailError"));
      return;
    }
    setIsViewRule(false);
    setUserEmailError();
    setIsViewUserAccess(true);
  };

  const reloadUiAfterSubmit = () => {
    setLoadRules(true);
  };

  return (
    <div className={classes.container}>
      {loading ? (
        renderLoadingIndicator()
      ) : (
        <Grid container>
          <Grid item xs={12}>
            <Grid container spacing={2} className={classes.userField}>
              <Grid item xs={12}>
                {t("labels.accessControl.rules.viewUserAccess")}
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="email"
                  name="email"
                  id="email"
                  fullWidth
                  placeholder={t(
                    "labels.accessControl.rule.form.enterUserEmail",
                  )}
                  hasError={userEmailError}
                  helpText={userEmailError}
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
              </Grid>
              <Grid item xs={2}>
                <MButton
                  color="primary"
                  variant="contained"
                  label={t("labels.accessControl.rules.viewUserAccess")}
                  //loading={loading || searching}
                  disabled={loading || searching}
                  onClick={searchUser}
                  style={{ height: 40 }}
                >
                  View User Access
                </MButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <RulesTable
              data={rules}
              canAddRule={canAddRule}
              canUpdateRule={canUpdateRule}
              canRemoveRule={canRemoveRule}
              onView={viewRule}
              onAdd={addRule}
              onDelete={onDelete}
            />
          </Grid>
        </Grid>
      )}
      {canViewRules && isViewRule && (
        <ViewRule
          entityCategories={entityCategories}
          entitySubCategories={entitySubCategories}
          geographiesData={geographiesData}
          countriesDataForAccessRole={countriesDataForAccessRole}
          processSequencesForAccessRole={processSequencesForAccessRole}
          allUserGroups={allUserGroups}
          reloadUiAfterSubmit={reloadUiAfterSubmit}
          open={isViewRule}
          onclose={handleClose}
          rule={currentRule}
        />
      )}
      {canAddRule && isNewRule && (
        <AddRule
          rules={rules}
          open={isNewRule}
          onclose={handleClose}
          reloadUiAfterSubmit={reloadUiAfterSubmit}
          entityCategories={entityCategories}
          entitySubCategories={entitySubCategories}
          geographiesData={geographiesData}
          countriesDataForAccessRole={countriesDataForAccessRole}
          processSequencesForAccessRole={processSequencesForAccessRole}
          allUserGroups={allUserGroups}
        />
      )}
      {canViewRules && isViewUserAccess && (
        <ViewUserAccess
          open={isViewUserAccess}
          onclose={handleClose}
          userEmail={userEmail}
        />
      )}
    </div>
  );
};

export default AccessControlRules;
